/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useMemo, useState } from 'react';
import Modal, { ModalProps } from 'antd/lib/modal';
import { DbAttachment, DbFile } from '~src/types/db';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useIntl } from 'react-intl';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { message, Spin, Tree, Typography } from 'antd';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DataNode } from 'antd/lib/tree';
import navigationItems from '~src/data/navigationItems';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import IconButton from '~components/_common/IconButton/IconButton';
import { arrToTree } from '~components/popups-modals/Move.modal';
import { uniq } from 'lodash';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import {
  changeFilesState,
  createCommit,
  deleteCommit,
  editFile,
  moveFiles,
  updateCommit,
  // moveFiles,
} from '~src/services/api';
import { DB_STATE } from '~src/types/enums';
import { appLog } from '~src/utils/log';
import { useProjectId } from '~src/hooks/useProjectId';
import { queryCache, useMutation } from 'react-query';

export interface RestoreToModalProps extends Partial<ModalProps> {
  files: DbFile[] | DbAttachment[];
  onDone?: () => void;
}

type StateDataNode = DataNode & {
  children?: StateDataNode[];
  parentId?: string | null;
};

const archiveKey = 'archive';
const archiveNode = navigationItems.find(
  (v) => v.key === archiveKey
) as typeof navigationItems[0];

export const RestoreToModal: React.FC<RestoreToModalProps> = ({
  files,
  onDone,

  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );

  const [selectedKeys, setSelectedKeys] = useState<string[]>([archiveKey]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([archiveKey]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [treeData, setTreeData] = useState<StateDataNode[]>([
    {
      title: intl.formatMessage({ id: archiveNode.nameTerm }),
      key: archiveNode.key,
      icon: (
        <IconButton
          icon={archiveNode.icon as IconProp}
          color={archiveNode.color}
          customStyle={styles.icons}
          backgroundOff
        />
      ),
    },
  ]);

  const filesQuery = useFiles(
    { folderId: selectedFolderId },
    { enabled: files.length > 0 && modalProps.visible }
  );

  const [doBulkRestore, mutationBulkRestore] = useMutation(changeFilesState, {
    onError: (error, { commit }) => {
      appLog('BulkRestoreFiles => ', error);
      message.error(intl.formatMessage({ id: 'restore-failed' }));
      commit &&
        deleteCommit({ projectId: String(projectId), commit }).catch(
          () => undefined
        );
      throw error;
    },
    onSuccess: async (data, { commit }) => {
      commit &&
        (await updateCommit({
          projectId: String(projectId),
          commitData: { ...commit, state: DB_STATE.PROCESSED },
        }));
      queryCache.invalidateQueries('files');
      // @ts-ignore
      onDone && onDone();
      message.success(intl.formatMessage({ id: 'restore-success' }));
      // setSelectedItems([]);
    },
  });

  const [doMoveRegistrations, motationMoveRegistrations] = useMutation(
    moveFiles,
    {
      onError: (error) => {
        appLog('MoveRegistrations => ', error);
        message.error(intl.formatMessage({ id: 'restore-failed' }));
        // @ts-ignore
        onDone && onDone();
      },
      onSuccess: (data, { commit, projectId, files }) => {
        // message.success('moved successfully');
        // queryCache.invalidateQueries('files');
        doBulkRestore({
          projectId: Number(projectId),
          registrationsId: files,
          stateValue: DB_STATE.AVAILABLE,
          commit,
        });
      },
    }
  );
  useEffect(() => {
    if (foldersQuery.data) {
      setTreeData((prevData) => {
        const nextData = [...prevData];

        nextData[0].children = arrToTree({
          arr: foldersQuery.data || [],
          parentId: null,
        });

        return nextData;
      });
    }
  }, [foldersQuery.data, isSelected, isValid]);

  const originalSelectedFolderId =
    selectedFolderId !== null ? Number(selectedFolderId) : selectedFolderId;

  const handleSelect = (keys: React.Key[]) => {
    setIsSelected(true);
    const strKeys = keys as string[];

    if (strKeys[0] === 'archive') {
      setIsSelected(false);
    }

    // triggers refetch of filesQuery
    setSelectedFolderId(strKeys[0] === archiveKey ? null : strKeys[0]);

    // set selected
    setSelectedKeys(strKeys);
    setExpandedKeys((prevKeys) => uniq([...prevKeys, ...strKeys]));
  };

  //   useEffect(() => {
  //     if (modalProps.visible) {
  //       setSelectedFolderId(folderId ? String(folderId) : null);

  //       const selectedKey = folderId ? String(folderId) : archiveKey;
  //       setSelectedKeys([selectedKey]);
  //       setExpandedKeys([selectedKey]);
  //     }
  //   }, [modalProps.visible, folderId]);

  const allowedDestination = useMemo(() => {
    // if (!foldersQuery.data || originalSelectedFolderId === null) return false;

    // const destinationFolder = foldersQuery.data.find(
    //   (v) => v.id === originalSelectedFolderId
    // );
    // if (!destinationFolder) return false;

    // return destinationFolder.permission >= RIGHTS_LEVEL.WRITE;
    return true;
  }, [foldersQuery.data, originalSelectedFolderId]);

  useEffect(() => {
    setIsValid(true);

    if (
      !allowedDestination ||
      !foldersQuery.data ||
      (!files.length && !filesQuery.data)
      //   ||
      //   (!files.length && !folde rs.length)
    ) {
      setIsValid(false);
    } else if (files.length) {
      // check for files with matching names in selected folder
      if (
        files.some((file) =>
          filesQuery.data?.some(
            (v) => v.id !== file.id && v.title === file.title
          )
        )
      ) {
        setIsValid(false);
      }
    }
  }, [handleSelect, filesQuery.data]);

  const doCreateCommit = async () => {
    if (!projectId) throw new Error('Missing project id.');

    const commitData = {
      description: `Move & Restore files for project id = ${projectId}`,
      // TO DO: Check if important should be true/false
      is_important: true,
      state: DB_STATE.DRAFT,
    };

    return createCommit({
      projectId: String(projectId),
      commitData,
    }).catch((err) => {
      message.error(intl.formatMessage({ id: 'commit-create-failed' }));
      appLog('Move & Restore files -> createCommit -> commit', commitData);
      appLog('Move & Restore files -> createCommit -> err', err);
      throw err;
    });
  };

  const onRestore = async () => {
    const commit = await doCreateCommit();
    if (
      (files as Array<DbFile | DbAttachment>).every(
        (file) => 'registration_id' in file
      )
    ) {
      // TypeScript now understands that every item in `files` has a `registration_id`,
      // implying that `files` is an array of `DbFile`.
      // Additional logic for DbFile[] can be added here
    } else {
      return doMoveRegistrations({
        projectId: Number(projectId),
        files: (files as Array<{ id: string | number }>).map((item) =>
          Number(item.id)
        ),
        folderId: Number(selectedFolderId),
        commit,
      });
    }
  };

  const getErrorMessage = () => {
    if (foldersQuery.isFetching) {
      return <Spin size="small" />;
    }

    if (!allowedDestination) {
      return intl.formatMessage({ id: 'permission-restricted' });
    }

    if (!isValid && isSelected) {
      return intl.formatMessage({ id: 'name-conflict' });
    }

    return null;
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'restore-modal-title' })}
          icon={['far', 'arrow-alt-from-left']}
        />
      }
      okButtonProps={{ disabled: !isSelected || !isValid }}
      centered
      width={MODAL_WIDTH}
      onOk={() => onRestore()}
      okText={intl.formatMessage({ id: 'restore' })}
      onCancel={() => onDone && onDone()}
      destroyOnClose
      {...modalProps}>
      <Typography.Paragraph css={styles.loadingContainer} type="danger">
        {getErrorMessage()}
      </Typography.Paragraph>
      <div css={styles.treeContainer}>
        <Tree.DirectoryTree
          css={styles.tree}
          blockNode
          treeData={treeData}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          expandAction={false} // don't auto expand on click
          autoExpandParent
          onSelect={handleSelect}
          onExpand={(keys) => setExpandedKeys(keys as string[])}
        />
      </div>
    </Modal>
  );
};

const MODAL_WIDTH = 420;
const TREE_WIDTH = MODAL_WIDTH - 45;

const styleFn: ThemedStyleFn = (theme) => ({
  icons: {
    fontSize: '20px',
    transition: '0.3s',
    margin: '3px 0 10px -16px',
  },
  treeContainer: {
    minHeight: '30vh',
    maxHeight: '39vh',
    width: TREE_WIDTH,
    maxWidth: TREE_WIDTH,
    overflow: 'auto',
  },
  tree: {
    width: '100%',
    overflow: 'hidden',
  },
  loadingContainer: {
    '&.ant-typography': {
      minHeight: 24,
      marginBottom: '0.7em',
      marginTop: -20,
    },
  },
});
